import { useState } from 'react';

/**
 * Lets a controlled numeric input hold a HALF-TYPED number, so a decimal point survives being typed.
 *
 * THE BUG THIS EXISTS TO PREVENT, because it is silent and it corrupts data. A controlled input whose
 * displayed value is the PARSED number cannot represent "7." — `parseFloat('7.')` is `7`, the parent
 * re-renders with `7`, and the input redraws as "7" with the point gone. The next keystroke therefore
 * appends to "7" rather than to "7.", so a user typing 7.5 into an interest rate ends up with 75, and
 * nothing anywhere says so. A projection then amortises a loan at 75% a year.
 *
 * The fix is to display the user's own text while they are typing, and to fall back to the canonical
 * value the moment they stop. The parent still receives a parsed number on every keystroke, so nothing
 * downstream changes; only what the box SHOWS differs, and only mid-edit.
 *
 * `syncOnBlur` is what re-attaches the field to the outside world: on blur the draft is dropped, so a
 * value the parent clamped, rounded or rejected is displayed rather than the user's raw text.
 */
/**
 * What the box shows, given what is being typed and what the field's value actually is.
 *
 * A pure function, and exported, so it can be tested without a renderer — the rule is the whole fix,
 * and a test that re-implemented it would have proved only that the copy agreed with itself.
 *
 * The draft is shown only while it still MEANS the same number as the canonical value. Without that
 * check the box would go stale: a strategy reset, a clamp, or a value changed elsewhere would leave the
 * last thing typed sitting on screen, describing a number the client file no longer holds. Comparing
 * numerically rather than by string is what lets "7." (7) and "0.50" (0.5) survive while a genuine
 * external change still wins.
 */
export function decimalDisplayValue(draft: string | null, value: number | string | null | undefined): string {
    const canonical = value === '' || value === undefined || value === null ? '' : String(value);
    if (draft === null) return canonical;
    const stillMeansTheSameNumber = draft === '' ? canonical === '' : Number(draft) === Number(canonical);
    return stillMeansTheSameNumber ? draft : canonical;
}

export function useDecimalDraft(value: number | string | null | undefined) {
    /** The raw text being typed, or null when the field is showing the canonical value. */
    const [draft, setDraft] = useState<string | null>(null);

    return {
        displayValue: decimalDisplayValue(draft, value),
        /** Record what was typed. Call alongside the parent's own onChange, not instead of it. */
        setDraft,
        /** Drop the draft so the canonical value shows again — wire to the input's onBlur. */
        syncOnBlur: () => setDraft(null),
    };
}
